import { Dialog } from '@angular/cdk/dialog';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  model,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  untracked,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { dia } from '@clientio/rappid';
import * as joint from '@clientio/rappid';
import { TranslocoModule } from '@jsverse/transloco';
import { filter } from 'rxjs';
import {
  FlowchartDragDropService,
  GuidelineViewService,
  InsightsService,
  SelectionService,
  TrackingEvent,
  TreeScrollService,
} from 'shared/domain';
import { IconDirective } from 'shared/ui-common';
import { IsMobileService } from 'shared/util-common';

import { CommentService } from '@treetop/shared/comments';
import { FlowchartNodeType, FlowchartStartNodeDto } from '@treetop/shared/model';

import { FlowchartDialogComponent } from './components/compare-flowchart-dialog/flowchart-dialog.component';
import { FlowchartToolbarComponent, FlowchartToolbarEvent } from './components/toolbar/toolbar.component';
import { getDroppedElementPosition } from './flowchart.component.util';
import { ConverterService } from './infrastructure/base/converter.service';
import { FlowchartEventsService } from './infrastructure/base/flowchart-events.service';
import { FlowchartSearchService } from './infrastructure/base/flowchart-search.service';
import { FlowchartConfig } from './infrastructure/base/flowchart.config';
import { FlowchartService } from './infrastructure/base/flowchart.service';
import { FlowchartElementsSubjectStore } from './infrastructure/data/flowchart-elements-subject-store.service';
import { FlowchartManagerService } from './infrastructure/data/flowchart-manager.service';
import { FlowchartModelService } from './infrastructure/flowchart/model.service';
import { FlowchartScrollerService } from './infrastructure/flowchart/scroller.service';
import { FlowchartViewService } from './infrastructure/flowchart/view.service';
import ElementActivitiesTransactionService from './infrastructure/operations/element-activities-toolbar/element-activities-transaction.service';
import { FlowchartLoaderService } from './infrastructure/operations/flowchart-loader.service';
import { NodePickerEvent } from './infrastructure/operations/node-picker-toolbar/node-picker-toolbar-items.config';
import NodePickerTransactionService from './infrastructure/operations/node-picker-toolbar/node-picker-transaction.service';
import OperationsService from './infrastructure/operations/operations.service';
import SelectionBehaviorService from './infrastructure/operations/selection-behavior.service';
import { SizeService } from './infrastructure/operations/size.service';
import { StartNodeService } from './infrastructure/operations/start-node.service';
import SubflowService from './infrastructure/operations/subflow.service';
import { FlowchartPaperRegistryService } from './services/flowchart-paper-registry.service';
import { AnchoredFlowchartComment } from '../../../tt-comments/src/lib/comment.model';

@Component({
  standalone: true,
  selector: 'tt-flowchart',
  templateUrl: './flowchart.component.html',
  styleUrls: ['./flowchart.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    FlowchartElementsSubjectStore,
    FlowchartService,
    FlowchartManagerService,
    FlowchartSearchService,
    FlowchartLoaderService,
    NodePickerTransactionService,
    FlowchartViewService,
    FlowchartModelService,
    FlowchartScrollerService,
    SelectionBehaviorService,
    StartNodeService,
    SubflowService,
    OperationsService,
    ElementActivitiesTransactionService,
    FlowchartEventsService,
    ConverterService,
  ],
  imports: [
    MatProgressSpinnerModule,
    TranslocoModule,
    MatTooltipModule,
    FlowchartToolbarComponent,
    MatIconModule,
    IconDirective,
    TranslocoModule,
  ],
})
export class FlowchartComponent implements OnInit, OnDestroy, AfterViewInit {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private treeScrollService = inject(TreeScrollService);
  private dialog = inject(Dialog);
  private viewContainerRef = inject(ViewContainerRef);
  private isMobileService = inject(IsMobileService);
  private insightsService = inject(InsightsService);
  private flowchartPaperRegistry = inject(FlowchartPaperRegistryService);
  private commentService = inject(CommentService, { optional: true });
  private guidelineViewService = inject(GuidelineViewService, { optional: true });
  private dragDropService = inject(FlowchartDragDropService);
  private nodePickerTransactionService = inject(NodePickerTransactionService);
  private selectionService = inject(SelectionService, { optional: true });
  protected flowchartService = inject(FlowchartService);
  protected sizeService = inject(SizeService);

  public flowchartId = input.required<string>();
  public flowchartElements = model<FlowchartNodeType[]>();
  public index = input<number>();

  @Input() public allowEvents: boolean;
  @Input() public fullScreen = false;

  @Output() public compareButtonClicked = new EventEmitter<void>();
  @Output() public exportButtonClicked = new EventEmitter<dia.Paper>();
  @Output() public exportPdfButtonClicked = new EventEmitter<dia.Paper>();
  @Output() public paperReady = new EventEmitter<{ flowchartId: string; paper: dia.Paper }>();

  @ViewChild('paper', { static: true }) public canvas!: ElementRef;

  protected component = new FormControl();
  protected intersectionObserver: IntersectionObserver;
  protected paper: joint.dia.Paper;

  private initialized = false;
  private viewReady = false;

  protected showOverlay = computed(() => !this.flowchartElements()?.length && !this.dragDropService.dragging() && this.allowEvents);

  private flowchartComments = computed(() => {
    if (!this.commentService) {
      return [];
    }

    return (
      this.commentService
        .comments()
        ?.filter((comment) => {
          return (
            comment.anchor.type === 'flowchart-element' &&
            comment.anchor.widgetId === this.flowchartId() &&
            !this.commentService?.isOriginalContentUnavailable(comment)
          );
        })
        .map((comment) => comment as AnchoredFlowchartComment) ?? []
    );
  });

  constructor() {
    effect(() => {
      const flowchartElements = this.flowchartService.flowchartElements();

      untracked(() => {
        if (this.allowEvents && this.initialized) {
          this.flowchartElements.set(flowchartElements);
        }
      });
    });

    effect(() => {
      const flowchartElements = this.flowchartElements();

      untracked(() => {
        if (!this.initialized) {
          this.flowchartService.setElements(flowchartElements);
          if ((!flowchartElements || flowchartElements.length === 0) && this.allowEvents) {
            this.selectionService?.selectWidget(this.flowchartId());
          }
        } else {
          this.flowchartService.updateElements(flowchartElements);
        }
      });
    });

    effect(() => {
      this.flowchartService.comments.set(this.flowchartComments());
    });

    effect(() => {
      if (!this.commentService) {
        return;
      }

      const selectedComment = this.commentService.selectedCommentData();
      if (!selectedComment) {
        this.flowchartService.selectedCommentId.set(undefined);
        return;
      }

      if (this.flowchartComments().find((comment) => comment.id === selectedComment.id)) {
        this.flowchartService.selectedCommentId.set(selectedComment.id);
        return;
      }

      this.flowchartService.selectedCommentId.set(undefined);
    });

    toObservable(this.index)
      .pipe(filter(() => this.viewReady))
      .subscribe(() => {
        this.focusStartNode();
      });
  }

  public ngOnInit(): void {
    this.sizeService.updatePaperHeight.subscribe((update) => {
      if (update) {
        this.setPaperHeight();
        this.sizeService.updatePaperHeight.next(false);
      }
    });
  }

  public ngAfterViewInit(): void {
    if (!this.allowEvents) {
      this.restartFlowchartView();
      this.viewReady = true;
      return;
    }

    this.restartFlowchartView();

    this.intersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        if (
          this.flowchartElements() &&
          !(this.flowchartElements()[0] as FlowchartStartNodeDto)?.guidelineAction &&
          !(this.flowchartElements()[0] as FlowchartStartNodeDto)?.guidelineCriteria &&
          !this.initialized
        ) {
          this.flowchartService.freezeFlowchartSubject.next(true);
          this.initialized = true;
        } else {
          this.flowchartService.freezeFlowchartSubject.next(entries[0]?.isIntersecting);
        }
      },
      { root: null, threshold: 0 },
    );

    const element = this.el.nativeElement as HTMLElement;
    this.intersectionObserver.observe(element);
    this.viewReady = true;
  }

  protected onToolbarEvent(event: FlowchartToolbarEvent): void {
    switch (event) {
      case FlowchartToolbarEvent.ZOOM_IN:
        this.flowchartService.scroller.zoom(FlowchartConfig.paper.ZOOM_STEP, { max: FlowchartConfig.paper.ZOOM_MAX });
        break;
      case FlowchartToolbarEvent.ZOOM_OUT:
        this.flowchartService.scroller.zoom(-FlowchartConfig.paper.ZOOM_STEP, { min: FlowchartConfig.paper.ZOOM_MIN });
        break;
      case FlowchartToolbarEvent.SCALE_CONTENT_TO_FIT:
        this.flowchartService.scroller.zoomToFit({ padding: 40 });
        break;
      case FlowchartToolbarEvent.FOCUS_START:
        this.focusStartNode();
        break;
      case FlowchartToolbarEvent.EXPORT:
        this.exportButtonClicked.emit(this.flowchartService.paper);
        break;
      case FlowchartToolbarEvent.EXPORT_PDF:
        this.exportPdfButtonClicked.emit(this.flowchartService.paper);
        break;
      case FlowchartToolbarEvent.EXPAND:
        this.insightsService.trackEvent(TrackingEvent.FLOWCHART_FULLSCREEN_OPENED);
        this.openFlowchartInDialog();
        break;
      default:
        break;
    }
  }

  protected onOverlayClick(event: MouseEvent): void {
    event.stopPropagation();
    this.selectionService?.selectWidget(this.flowchartId());
  }

  public handlePaperClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  public handleDragOver(event: DragEvent): void {
    if (!this.allowEvents || !this.dragDropService.dragging()) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  public handleDrop(event: DragEvent): void {
    if (!this.allowEvents || !this.dragDropService.dragging()) return;
    event.preventDefault();

    const elementType = this.dragDropService.dragging();
    if (!elementType) return;

    const position = getDroppedElementPosition(this.flowchartService.paper, event, elementType);

    this.createStandaloneElement(elementType, position);
    this.dragDropService.endDrag();
  }

  private openFlowchartInDialog(): void {
    this.dialog.open(FlowchartDialogComponent, {
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      hasBackdrop: true,
      disableClose: true,
      panelClass: 'tt-dialog--full-screen',
      data: {
        flowchartElements: this.flowchartElements(),
        id: this.flowchartId(),
      },
      viewContainerRef: this.viewContainerRef,
    });
  }

  private focusStartNode(): void {
    const startNodeCellBbox = this.flowchartService.graph.getFirstCell().getBBox();
    this.flowchartService.scroller.zoomToRect(startNodeCellBbox, { padding: 40 });
    this.flowchartService.paper.scale(1, 1);
  }

  private createStandaloneElement(event: NodePickerEvent, position: { x: number; y: number }): void {
    const isFirstElement = this.flowchartService.graph.getElements().length === 0;

    if (isFirstElement) {
      this.flowchartService.prepareViewportUpdate(position);
    }

    const element = this.nodePickerTransactionService.createStandaloneElement(event, position);
    this.flowchartService.paper.updateViews();
    this.setPaperHeight();

    if (isFirstElement) {
      this.flowchartService.finalizeViewportUpdate(position);
    }

    const showsPopover =
      event === NodePickerEvent.ADD_ACTION || event === NodePickerEvent.ADD_DECISION || event === NodePickerEvent.ADD_SUBFLOW;
    if (showsPopover) {
      window.setTimeout(() => {
        this.nodePickerTransactionService.openSearchPopoverForDraggedElement(event, element);
      }, 250);
    }
  }

  private setPaperHeight(): void {
    const canvasElement: HTMLElement = this.canvas.nativeElement;
    this.paper = this.flowchartService.paper;

    if (this.paper) {
      this.flowchartPaperRegistry.register(this.flowchartId(), this.paper);
    }

    const element = this.el.nativeElement;
    let paperHeight = this.paper.getContentBBox({ useModelGeometry: true }).height;
    if (this.allowEvents && paperHeight < FlowchartConfig.paper.baseHeight) {
      paperHeight = FlowchartConfig.paper.baseHeight;
    }

    if (!this.allowEvents) {
      paperHeight += Math.ceil(FlowchartConfig.paper.padding / 2 / FlowchartConfig.paper.gridSize) * FlowchartConfig.paper.gridSize;
    }

    if (!this.fullScreen) {
      this.renderer.setStyle(element, 'height', `${paperHeight}px`);
      canvasElement.style.height = `${paperHeight}px`;
    }

    this.treeScrollService.updateScrollOnChanges();
  }

  private restartFlowchartView(): void {
    const canvasElement: HTMLElement = this.canvas.nativeElement;
    this.flowchartService.init(this.flowchartId(), canvasElement, {
      allowEvents: this.allowEvents,
      mobileView: !this.fullScreen && this.isMobileService.isMobile(),
      allowInlineComments:
        this.guidelineViewService?.isSmartbookMode() ||
        this.guidelineViewService?.isPreviewMode() ||
        this.guidelineViewService?.isReviewMode(),
    });
    this.setPaperHeight();
    if (!this.allowEvents && this.paper) {
      this.paperReady.emit({ flowchartId: this.flowchartId(), paper: this.paper });
    }

    this.initialized = true;
  }

  ngOnDestroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.flowchartService.unsubscribe();
    this.flowchartPaperRegistry.unregister(this.flowchartId());
  }
}
